'use client';

/**
 * VendorResponseActions - Quick actions for vendor quotes/responses
 *
 * Available actions:
 * - Convert to PO (if submitted)
 * - Contact Vendor (WhatsApp)
 * - Request Revision
 * - Open RFQ Comparison
 * - View in Odoo
 *
 * @component_role VendorResponse-level Actions
 * @business_rule Vendor response workflow: Convert quoted response to PO,
 *   contact vendor for clarification, request revised pricing, compare with
 *   other responses. "Open in Odoo" links to the vendor's purchase order form.
 * @user_outcome Act on vendor quotes immediately - convert winning bids to POs
 *   or follow up on pricing questions without switching contexts.
 * @integration_point klyde_edge /po/create, /messaging/whatsapp/send, Odoo deep links
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import {
  FileCheck,
  ShoppingCart,
  MessageSquare,
  RefreshCw,
  GitCompare,
  ExternalLink,
  ChevronRight,
  Factory,
} from 'lucide-react';
import { toast } from 'sonner';
import { useVendorResponseExplore } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

interface VendorResponseActionsProps {
  responseCdk: string;
  projectId?: string | null;
}

export function VendorResponseActions({ responseCdk, projectId }: VendorResponseActionsProps) {
  const vendorResponseStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_response'));
  const { data } = useVendorResponseExplore(vendorResponseStrategy!, { response_cdk: responseCdk });
  const response = data?.response;

  const ODOO_HOST = process.env.NEXT_PUBLIC_ODOO_URL || 'http://klyde.vivita-ai.com';

  const actions = [
    {
      label: 'Convert to PO',
      description: response?.total_amount ? `Create PO for $${response.total_amount.toFixed(0)}` : 'Create purchase order',
      icon: ShoppingCart,
      onClick: () => {
        toast.info('Convert to PO', {
          description: 'PO creation from response coming soon',
        });
        // TODO: Trigger PO creation with pre-filled line items from response
      },
      primary: true,
      disabled: response?.state !== 'submitted',
    },
    {
      label: 'Contact Vendor',
      description: response?.vendor?.name || 'Send WhatsApp message',
      icon: MessageSquare,
      onClick: () => {
        toast.info('Contact Vendor', {
          description: 'Switching to Chat tab...',
        });
        // TODO: Trigger tab switch to Chat with vendor pre-selected
      },
    },
    {
      label: 'Request Revision',
      description: 'Ask for updated pricing or terms',
      icon: RefreshCw,
      onClick: () => {
        toast.info('Request Revision', {
          description: 'Revision request coming soon',
        });
      },
    },
    {
      label: 'Compare Responses',
      description: response?.rfq?.name || 'View RFQ comparison matrix',
      icon: GitCompare,
      onClick: () => {
        toast.info('Compare Responses', {
          description: 'Opening RFQ comparison view...',
        });
        // TODO: Navigate to RFQ comparison view
      },
    },
    {
      label: 'Open in Odoo',
      description: 'View full response details',
      icon: ExternalLink,
      onClick: () => {
        if (response?.odoo_id) {
          window.open(`${ODOO_HOST}/web#id=${response.odoo_id}&model=purchase.order&view_type=form`, '_blank');
        } else {
          toast.error('Cannot open', { description: 'Response ID not available' });
        }
      },
      disabled: !response?.odoo_id,
    },
  ];

  return (
    <div className="space-y-4 p-3">
      {/* Header */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">RESPONSE ACTIONS</h4>
        <div className="p-2 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2">
            <FileCheck className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium truncate">
              {response?.vendor?.name || 'Vendor Response'}
            </span>
          </div>
          {response?.total_amount != null && (
            <div className="text-xs text-success mt-1">
              Quoted: ${response.total_amount.toFixed(2)}
            </div>
          )}
          {response?.state && (
            <div className="text-xs text-muted-foreground mt-1 capitalize">
              Status: {response.state}
            </div>
          )}
        </div>
      </div>

      {/* Vendor Info */}
      {response?.vendor && (
        <div className="p-2 rounded-lg bg-accent/50 border border-border">
          <div className="flex items-center gap-2">
            <Factory className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">{response.vendor.name}</div>
              {response.vendor.type && (
                <div className="text-xs text-muted-foreground capitalize">{response.vendor.type}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions List */}
      <div className="space-y-2">
        {actions.map((action, i) => (
          <button
            key={i}
            onClick={action.onClick}
            disabled={action.disabled}
            className={`
              flex items-center justify-between w-full p-3 rounded-lg
              transition-colors text-left
              ${action.primary
                ? 'bg-primary/10 hover:bg-primary/20 border border-primary/20'
                : 'hover:bg-accent border border-transparent'
              }
              ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-md ${action.primary ? 'bg-primary/20' : 'bg-muted'}`}>
                <action.icon className={`h-4 w-4 ${action.primary ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <div className="text-sm font-medium">{action.label}</div>
                <div className="text-xs text-muted-foreground truncate max-w-[180px]">{action.description}</div>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}
